import {
  ApiError,
  applyPlanningProposals,
  applyStoryProposal,
  askCurriculum,
  updatePlannedModule,
} from '@platform/client';
import { defined } from '@platform/schema';
import {
  appliedProposalsSchema,
  curriculumAnswerSchema,
} from '@platform/schema/chat';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useState } from 'react';

import { useTrackedMutation } from '../actions/ActionsProvider';
import { describeApiError } from '../api-error';

import { castBody } from './cast';
import { outcomes, turnsFor } from './chat';

import type { Outcome } from './chat';
import type {
  ChatTurn,
  CurriculumAnswer,
  PlanningScope,
} from '@platform/schema/chat';
import type { ReactNode } from 'react';

export type PanelMode = 'open' | 'collapsed' | 'closed';

/** One turn, and for an answer, what became of each proposal in it, its story and cast. */
export type Said =
  | { question: string }
  | {
      answer: CurriculumAnswer;
      settled?: Record<number, Outcome>;
      story?: Outcome;
      cast?: Outcome;
    };

const turnOf = (one: Said): ChatTurn =>
  'question' in one
    ? { role: 'operator', text: one.question }
    : { role: 'planner', text: one.answer.text };

interface AskAi {
  readonly mode: PanelMode;
  readonly setMode: (mode: PanelMode) => void;
  /** What the conversation is about. It changes only when the admin changes it. */
  readonly scope: PlanningScope;
  readonly setScope: (scope: PlanningScope) => void;
  /** A lesson a row click offered as the new scope, waiting for the admin's answer. */
  readonly proposed: string | null;
  readonly propose: (lessonId: string | null) => void;
  readonly said: readonly Said[];
  /** Bumped by "AI edit": the question box takes focus and asks for an instruction. */
  readonly instructing: number;
  readonly instruct: () => void;
  /** `onRefused` gets the question back, so an unanswered one can be asked again. */
  readonly ask: (text: string, onRefused: (text: string) => void) => void;
  readonly asking: boolean;
  readonly apply: (at: number, which: readonly number[]) => void;
  readonly applying: boolean;
  readonly dismiss: (at: number, which: number) => void;
  /** The answer's module story, accepted as one story revision — CURR-41. */
  readonly acceptStory: (at: number) => void;
  readonly dismissStory: (at: number) => void;
  /** The answer's module cast, saved through the module's story revision — CURR-44. */
  readonly acceptCast: (at: number) => void;
  readonly dismissCast: (at: number) => void;
  /** Builds started from Curriculum; a reload finds running ones from their lessons. */
  readonly started: readonly string[];
  readonly addStarted: (jobId: string) => void;
}

/** A write the API refused is an outcome to show; anything else is an error. */
async function refusedOr(write: () => Promise<unknown>): Promise<Outcome> {
  try {
    await write();
    return { state: 'applied' };
  } catch (failure) {
    if (!(failure instanceof ApiError) || failure.status >= 500) throw failure;
    return {
      state: 'refused',
      reason: failure.status === 404 ? 'missing' : 'stale',
      message: describeApiError(failure),
    };
  }
}

const AskAiContext = createContext<AskAi | undefined>(undefined);

/**
 * The AI panel's state, held above the routes — CURR-30. Collapsing the panel or visiting
 * another page unmounts it, and the conversation, an answer still on its way, and the
 * builds it started must all be there on return. A reload starts a new conversation.
 */
export function AskAiProvider({ children }: { children: ReactNode }) {
  const queries = useQueryClient();
  const [mode, setMode] = useState<PanelMode>('closed');
  const [scope, setScope] = useState<PlanningScope>({ kind: 'curriculum' });
  const [proposed, propose] = useState<string | null>(null);
  const [said, setSaid] = useState<Said[]>([]);
  const [instructing, setInstructing] = useState(0);
  const [started, setStarted] = useState<readonly string[]>([]);

  const asked = useTrackedMutation('Ask about the curriculum', {
    mutationFn: async (text: string) => {
      const one: Said = { question: text };
      const turns = turnsFor(said.map(turnOf), text);
      setSaid((before) => [...before, one]);
      try {
        const answered = await askCurriculum({
          body: { turns, scope },
          throwOnError: true,
        });
        return curriculumAnswerSchema.parse(answered.data);
      } catch (failure) {
        // Left in, a question the planner never answered would be re-sent forever.
        setSaid((before) => before.filter((was) => was !== one));
        throw failure;
      }
    },
    onSuccess: (answer) => {
      setSaid((before) => [...before, { answer }]);
    },
  });

  const patchAnswer = (
    at: number,
    patch: (one: Extract<Said, { answer: CurriculumAnswer }>) => Said,
  ): void => {
    setSaid((before) =>
      before.map((one, index) =>
        index === at && 'answer' in one ? patch(one) : one,
      ),
    );
  };
  const settle = (at: number, settled: Record<number, Outcome>): void => {
    patchAnswer(at, (one) => ({
      ...one,
      settled: { ...one.settled, ...settled },
    }));
  };
  const settleStory = (at: number, story: Outcome): void => {
    patchAnswer(at, (one) => ({ ...one, story }));
  };
  const settleCast = (at: number, cast: Outcome): void => {
    patchAnswer(at, (one) => ({ ...one, cast }));
  };
  const answerAt = (at: number): CurriculumAnswer | undefined => {
    const one = said[at];
    return one !== undefined && 'answer' in one ? one.answer : undefined;
  };

  const applied = useTrackedMutation('Apply AI proposals', {
    mutationFn: async ({
      at,
      which,
    }: {
      at: number;
      which: readonly number[];
    }) => {
      const one = said[at];
      const proposals =
        one !== undefined && 'answer' in one ? one.answer.proposals : [];
      const result = appliedProposalsSchema.parse(
        (
          await applyPlanningProposals({
            body: {
              proposals: which.flatMap((n) => {
                const proposal = proposals[n];
                return proposal === undefined
                  ? []
                  : [
                      {
                        ...proposal,
                        before: defined(proposal.before),
                        after: defined(proposal.after),
                      },
                    ];
              }),
            },
            throwOnError: true,
          })
        ).data,
      );
      return { at, settled: outcomes(which, proposals, result) };
    },
    onSuccess: async ({ at, settled }) => {
      settle(at, settled);
      await queries.invalidateQueries({ queryKey: ['curriculum'] });
    },
  });

  const accepted = useTrackedMutation('Accept the AI module story', {
    mutationFn: async (at: number): Promise<Outcome> => {
      const [proposal] = answerAt(at)?.storyProposals ?? [];
      if (proposal === undefined)
        return {
          state: 'refused',
          reason: 'missing',
          message: 'The story is gone.',
        };
      return refusedOr(() =>
        applyStoryProposal({ body: proposal, throwOnError: true }),
      );
    },
    onSuccess: async (story, at) => {
      settleStory(at, story);
      await queries.invalidateQueries({ queryKey: ['curriculum'] });
    },
  });

  const castAccepted = useTrackedMutation('Accept the AI module cast', {
    mutationFn: async (at: number): Promise<Outcome> => {
      const [proposal] = answerAt(at)?.castProposals ?? [];
      if (proposal === undefined)
        return {
          state: 'refused',
          reason: 'missing',
          message: 'The cast is gone.',
        };
      return refusedOr(() =>
        updatePlannedModule({
          path: { id: proposal.moduleId },
          body: {
            cast: castBody(proposal.after),
            lessonCasts: proposal.lessons.map(({ lessonId, after }) => ({
              lessonId,
              cast: after,
            })),
            storyRevision: proposal.storyRevision,
          },
          throwOnError: true,
        }),
      );
    },
    onSuccess: async (cast, at) => {
      settleCast(at, cast);
      await queries.invalidateQueries({ queryKey: ['curriculum'] });
    },
  });

  const value: AskAi = {
    mode,
    setMode,
    scope,
    setScope: (next) => {
      setScope(next);
      propose(null);
    },
    proposed,
    propose,
    said,
    instructing,
    instruct: () => {
      setInstructing((was) => was + 1);
    },
    ask: (text, onRefused) => {
      asked.mutate(text, {
        onError: () => {
          onRefused(text);
        },
      });
    },
    asking: asked.isPending,
    apply: (at, which) => {
      applied.mutate({ at, which });
    },
    applying: applied.isPending || accepted.isPending || castAccepted.isPending,
    dismiss: (at, which) => {
      settle(at, { [which]: { state: 'dismissed' } });
    },
    acceptStory: (at) => {
      accepted.mutate(at);
    },
    dismissStory: (at) => {
      settleStory(at, { state: 'dismissed' });
    },
    acceptCast: (at) => {
      castAccepted.mutate(at);
    },
    dismissCast: (at) => {
      settleCast(at, { state: 'dismissed' });
    },
    started,
    addStarted: (jobId) => {
      setStarted((was) => [...new Set([...was, jobId])]);
    },
  };

  return (
    <AskAiContext.Provider value={value}>{children}</AskAiContext.Provider>
  );
}

export function useAskAi(): AskAi {
  const context = useContext(AskAiContext);
  if (context === undefined)
    throw new Error('useAskAi needs an AskAiProvider above it.');
  return context;
}
